import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    ResponsiveContainer,
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
} from 'recharts';
import { format, parseISO } from 'date-fns';
import { useBoardChartData } from '../../hooks/useBoardChartData';
import { BaseChartProps } from './types';
import { CHART_COLORS, CHART_STYLES, getCurrencySymbol, formatCompactNumber, formatCurrency } from './chartTheme';
import { useSetChartMetadata } from './ChartContext';

export const AdSpendVsRevenueTimingChart: React.FC<BaseChartProps> = () => {
    const { t } = useTranslation();
    const { finance, currencySettings } = useBoardChartData();

    const currencySymbol = useMemo(
        () => getCurrencySymbol(currencySettings?.default),
        [currencySettings?.default]
    );

    const chartData = useMemo(() => {
        if (!finance?.dailyBreakdown) return [];
        return Object.entries(finance.dailyBreakdown)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([date, day]: [string, Record<string, unknown>]) => {
                const metrics = day.metrics as Record<string, number> | undefined;
                return {
                    date,
                    adSpend: metrics?.adSpend ?? 0,
                    revenue: metrics?.revenue ?? 0,
                };
            });
    }, [finance?.dailyBreakdown]);

    const isEmpty = useMemo(() => chartData.length === 0, [chartData]);

    useSetChartMetadata({ isEmpty });

    if (isEmpty) return null;

    return (
        <div className="w-full h-full bg-transparent overflow-hidden flex flex-col">
            <div className="flex-1 min-h-0 p-2">
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData} margin={CHART_STYLES.margin.withAxis}>
                        <CartesianGrid
                            vertical={false}
                            stroke={CHART_STYLES.grid.stroke}
                            strokeDasharray={CHART_STYLES.grid.strokeDasharray}
                            strokeWidth={CHART_STYLES.grid.strokeWidth}
                            opacity={CHART_STYLES.grid.opacity}
                        />
                        <XAxis
                            dataKey="date"
                            tickFormatter={(v: string) => format(parseISO(v), 'MMM d')}
                            {...CHART_STYLES.axis}
                        />
                        <YAxis
                            tickFormatter={(v: number) => formatCompactNumber(v, currencySymbol)}
                            {...CHART_STYLES.axis}
                        />
                        <Tooltip
                            contentStyle={CHART_STYLES.tooltip.contentStyle}
                            cursor={CHART_STYLES.tooltip.cursor}
                            itemStyle={CHART_STYLES.tooltip.itemStyle}
                            formatter={(value: number, name: string) => [
                                formatCurrency(value, currencySymbol),
                                name,
                            ]}
                            labelFormatter={(label: string) => format(parseISO(label), 'MMM d, yyyy')}
                        />
                        <Legend wrapperStyle={{ fontSize: '10px' }} />
                        <Line
                            type="monotone"
                            dataKey="adSpend"
                            name={t('charts.adSpend', 'Ad Spend')}
                            stroke={CHART_COLORS.expense}
                            strokeWidth={CHART_STYLES.line.strokeWidth}
                            dot={false}
                            activeDot={CHART_STYLES.line.activeDot}
                        />
                        <Line
                            type="monotone"
                            dataKey="revenue"
                            name={t('charts.revenue', 'Revenue')}
                            stroke={CHART_COLORS.revenue}
                            strokeWidth={CHART_STYLES.line.strokeWidth}
                            dot={false}
                            activeDot={{ ...CHART_STYLES.line.activeDot, fill: CHART_COLORS.revenue }}
                        />
                    </LineChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};
